import { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBooking } from '@/contexts/BookingContext';
import SectionNavigationDots from '@/components/widgets/SectionNavigationDots';
import { useI18n } from '@/contexts/I18nContext';
import GoogleReviewBadge from '@/components/GoogleReviewBadge';
import TrustBar from '@/components/trust/TrustBar';

// Hero banner images (WebP-optimized, 1920w)
import heroBannerMountains from '@/assets/hero-banner-mountains.webp';
import heroBannerAbstract from '@/assets/hero-banner-abstract.webp';
import heroBannerLake from '@/assets/hero-banner-lake.webp';
const HERO_IMAGES = [heroBannerMountains, heroBannerAbstract, heroBannerLake];

// Word-by-word text reveal — smoother, more cinematic than per-letter
const LetterReveal = ({
  text,
  className,
  delay = 0
}: {
  text: string;
  className?: string;
  delay?: number;
}) => {
  const words = text.split(' ');
  return (
    <span className={className}>
      {words.map((word, index) => (
        <span key={index}>
          <span className="inline-block overflow-hidden align-bottom">
            <motion.span
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: '0%' }}
              transition={{
                duration: 1.1,
                delay: delay + index * 0.12,
                ease: [0.22, 1, 0.36, 1]
              }}
              className="inline-block will-change-transform"
            >
              {word}
            </motion.span>
          </span>
          {index < words.length - 1 ? '\u00A0' : ''}
        </span>
      ))}
    </span>
  );
};

// Typewriter effect for subtitles
const TypewriterText = ({
  text,
  delay = 0
}: {
  text: string;
  delay?: number;
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  useEffect(() => {
    const timeout = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1));
          index++;
        } else {
          clearInterval(interval);
          setTimeout(() => setShowCursor(false), 500);
        }
      }, 30);
      return () => clearInterval(interval);
    }, delay * 1000);
    return () => clearTimeout(timeout);
  }, [text, delay]);
  return <span>
      {displayedText}
      {showCursor && <span className="animate-pulse">|</span>}
    </span>;
};


const CinematicHero = () => {
  const { openBooking } = useBooking();
  const { t, localizedPath } = useI18n();
  const sn = t.sectionNav;
  const SECTIONS = [
    { id: 'hero', label: sn.home },
    { id: 'servicios-section', label: sn.services },
    { id: 'process-section', label: sn.process },
    { id: 'testimonials-section', label: sn.testimonials },
    { id: 'cta-section', label: sn.contact },
  ];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 100]);

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % HERO_IMAGES.length);
    }, 6000); // Change every 6 seconds
    return () => clearInterval(interval);
  }, []);


  // Scroll to content function
  const scrollToContent = () => {
    const servicesSection = document.getElementById('servicios-section');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <section id="hero" ref={containerRef} className="relative h-dvh min-h-[640px] w-full overflow-hidden bg-cinema-dark">
        {/* Video/Animated Background with rotating images */}
        <motion.div className="absolute inset-0 z-0" style={{
        scale: scale as any
      }}>
          {/* Rotating hero images */}
          <AnimatePresence mode="sync">
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1.15 }}
              exit={{ opacity: 0, scale: 1.2 }}
              transition={{
                opacity: { duration: 2.2, ease: [0.22, 1, 0.36, 1] },
                scale: { duration: 8, ease: 'linear' }
              }}
              className="absolute inset-0"
            >
              <img
                src={HERO_IMAGES[currentImageIndex]}
                alt="Punto Hispano - Suiza"
                width={1920}
                height={1080}
                {...({ fetchpriority: currentImageIndex === 0 ? 'high' : 'auto' } as any)}
                loading={currentImageIndex === 0 ? 'eager' : 'lazy'}
                decoding="async"
                className="w-full h-full object-cover will-change-transform"
              />
            </motion.div>
          </AnimatePresence>

          {/* Animated gradient background (overlay on images) */}
          <div className="absolute inset-0 bg-gradient-to-br from-cinema-dark/60 via-black/40 to-cinema-dark/60">
            {/* Animated gradient orbs — lighter on mobile to save GPU (huge blurs kill INP on Android) */}
            <motion.div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] md:w-[800px] md:h-[800px] bg-punto/20 rounded-full blur-[80px] md:blur-[200px]" animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.6, 0.3],
            x: [0, 50, 0],
            y: [0, -30, 0]
          }} transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }} />
            <motion.div className="absolute bottom-1/3 left-1/4 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-blue-600/10 rounded-full blur-[60px] md:blur-[150px]" animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
            x: [0, -40, 0]
          }} transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }} />
            <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[500px] md:h-[500px] bg-amber-500/10 rounded-full blur-[60px] md:blur-[120px]" animate={{
            scale: [1, 1.2, 1],
            opacity: [0.15, 0.3, 0.15]
          }} transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }} />
          </div>
          
          {/* God rays effect — desktop only (5 animated gradients, expensive on mobile) */}
          <div className="absolute inset-0 overflow-hidden hidden md:block">
            {[...Array(5)].map((_, i) => <motion.div key={i} className="absolute top-0 w-1 bg-gradient-to-b from-white/5 via-white/2 to-transparent" style={{
            left: `${20 + i * 15}%`,
            height: '100%',
            transform: `rotate(${-15 + i * 5}deg)`,
            transformOrigin: 'top center'
          }} animate={{
            opacity: [0, 0.3, 0],
            scaleY: [0.5, 1, 0.5]
          }} transition={{
            duration: 8 + i * 2,
            repeat: Infinity,
            delay: i * 0.5,
            ease: "easeInOut"
          }} />)}
          </div>
          
          {/* Vignette overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.7)_100%)]" />
        </motion.div>

        {/* Film grain overlay */}
        <div className="absolute inset-0 z-10 pointer-events-none opacity-[0.03] mix-blend-overlay" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`
      }} />

        {/* Cinematic scan lines */}
        <div className="absolute inset-0 z-10 pointer-events-none opacity-[0.02]" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)'
      }} />

        {/* Floating Particles — desktop only (20 animated dots hurt mobile INP) */}
        <div className="absolute inset-0 z-20 overflow-hidden pointer-events-none hidden md:block">
          {[...Array(20)].map((_, i) => <motion.div key={i} className={`absolute rounded-full ${i % 3 === 0 ? 'w-2 h-2 bg-punto/40' : 'w-1 h-1 bg-white/30'}`} style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`
        }} animate={{
          y: [0, -100, 0],
          x: [0, Math.random() * 50 - 25, 0],
          opacity: [0.1, 0.8, 0.1],
          scale: [0.5, 1, 0.5]
        }} transition={{
          duration: 6 + Math.random() * 6,
          repeat: Infinity,
          delay: Math.random() * 3,
          ease: "easeInOut"
        }} />)}
        </div>

        {/* Lens flare */}
        <motion.div className="absolute top-1/4 right-1/3 w-4 h-4 z-20 pointer-events-none" animate={{
        opacity: [0, 0.8, 0],
        scale: [0.5, 1.5, 0.5]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        delay: 2
      }}>
          <div className="absolute inset-0 bg-white rounded-full blur-sm" />
          <div className="absolute -inset-4 bg-punto/30 rounded-full blur-xl" />
          <div className="absolute -inset-8 bg-amber-500/20 rounded-full blur-2xl" />
        </motion.div>

        {/* Main Content */}
        <motion.div className="relative z-30 h-full flex flex-col justify-center" style={{
        opacity: opacity as any,
        y: y as any
      }}>
          <div className="container mx-auto px-4">
            <div className="max-w-5xl">
              {/* Film chapter indicator */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-4 mb-8"
              >
                <span className="font-cinzel text-sm tracking-[0.3em] uppercase text-destructive">{t.hero.act}</span>
                <div className="w-16 h-px bg-punto/50" />
                <span className="text-white/50 text-sm tracking-wider">{t.hero.actSubtitle}</span>
              </motion.div>
              
              {/* Main Title with Word Reveal */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-cinzel font-bold text-white leading-[0.95] mb-6">
                <div className="block">
                  <LetterReveal text={t.hero.titleLine1} delay={0.6} className="block" />
                </div>
                <div className="block">
                  <LetterReveal text={t.hero.titleLine2} delay={1.2} className="block text-punto" />
                </div>
                <div className="block">
                  <LetterReveal text={t.hero.titleLine3} delay={1.8} className="block" />
                </div>
              </h1>
              
              {/* Typewriter Subtitle */}
              <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 2,
              duration: 0.5
            }} className="text-white/60 text-lg md:text-xl lg:text-2xl max-w-2xl mb-10 font-light">
                <TypewriterText text={t.hero.cinematicSubtitle} delay={2.3} />
              </motion.p>

              {/* 4 CTA Buttons */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 3,
              duration: 0.8
            }} className="flex flex-wrap gap-3 mb-10">
                <Link to={localizedPath('/servicios')}>
                  <Button size="lg" variant="outline" className="px-3 sm:px-6 py-5 text-xs sm:text-base font-semibold bg-white/5 border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 rounded-lg transition-all group backdrop-blur-sm">
                    <Compass className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 group-hover:rotate-45 transition-transform" />
                    <span>{t.hero.discoverServices}</span>
                  </Button>
                </Link>

                <Button size="lg" onClick={() => openBooking()} className="px-3 sm:px-6 py-5 text-xs sm:text-base font-bold bg-punto hover:bg-punto/90 text-white rounded-lg border-2 border-punto hover:border-white/30 shadow-lg shadow-punto/30 hover:shadow-xl hover:shadow-punto/40 transition-all group">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  <span>{t.hero.scheduleConsultation}</span>
                  <motion.span className="ml-1 sm:ml-2 inline-block" animate={{
                  x: [0, 5, 0]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity
                }}>
                    →
                  </motion.span>
                </Button>

                {/* WhatsApp - Solo icono */}
                <a href="https://wa.me/41772317288" target="_blank" rel="noopener noreferrer" aria-label="Contactar por WhatsApp">
                  <Button size="lg" variant="outline" aria-label="Contactar por WhatsApp" className="px-4 sm:px-5 py-5 bg-white/5 border-2 border-white/20 text-white hover:bg-green-500/20 hover:border-green-500/50 rounded-lg transition-all group backdrop-blur-sm">
                    <MessageCircle className="w-5 h-5 group-hover:text-green-400 transition-colors" aria-hidden="true" />
                  </Button>
                </a>
              </motion.div>

              {/* Trust Bar: Google Reviews + FINMA + CICERO + Swiss Made */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 3.5,
              duration: 0.8
            }}>
                <TrustBar variant="hero-dark" />
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Section Navigation Dots - Extracted to widget */}
        <SectionNavigationDots sections={SECTIONS} />
      </section>
    </>
  );
};

export default CinematicHero;